'use strict'

import React, { useCallback, useMemo } from 'react'
import jsep from 'jsep'
import { ChrofyShowErrors } from '../../../common/Form'
import { ChrofyCustomSelect } from '../../../common/ChrofyCustomSelect'
import { getMessage } from '../../../../common/utils/MessageUtils'

// --- Validation messages (SR-125, SR-126, SR-127)
const MSG_EXPRESSION_REQUIRED = getMessage('E010001', '計算式')
const MSG_INVALID_EXPRESSION = getMessage('E010002', '計算式')

/**
 * @typedef {{ type: 'number'; value: string }} TokenNumber
 *
 * @typedef {{ type: 'operator'; value: '+' | '-' | '*' | '/' }} TokenOperator
 *
 * @typedef {{ type: 'paren'; value: '(' | ')' }} TokenParen
 *
 * @typedef {{ type: 'col'; fileType: string; columnName: string }} TokenCol
 *
 * @typedef {{ type: 'string'; value: string }} TokenString
 *
 * @typedef {TokenNumber | TokenOperator | TokenParen | TokenCol | TokenString} Token
 */

/**
 * MappingInfo for FourArithmeticExpressions (SR-111). JSEP AST as JSON string.
 *
 * @typedef {{ MappingType: 'FourArithmeticExpressions'; FourArithmeticExpressions: { expressionAst?: string } }} FourArithmeticMappingInfo
 */

/**
 * Escape single quotes for use inside single-quoted string
 *
 * @param {string} s
 * @returns {string}
 */
function escapeSingleQuotes(s) {
  return String(s).replace(/'/g, "\\'")
}

/**
 * Build expression string from tokens for JSEP (col as col('fileType','columnName')) (SR-113)
 *
 * @param {Token[]} tokens
 * @returns {string}
 */
function tokensToExpressionString(tokens) {
  const parts = []
  for (const t of tokens) {
    if (t.type === 'number') {
      parts.push(t.value)
    } else if (t.type === 'operator') {
      parts.push(t.value)
    } else if (t.type === 'paren') {
      parts.push(t.value)
    } else if (t.type === 'col') {
      const ft = escapeSingleQuotes(t.fileType || '')
      const cn = escapeSingleQuotes(t.columnName || '')
      parts.push(`col('${ft}','${cn}')`)
    } else if (t.type === 'string') {
      parts.push(`'${escapeSingleQuotes(t.value)}'`)
    }
  }
  return parts.join(' ')
}

/**
 * Build preview display string from tokens (SR-122) col tokens are displayed as [fileType.columnName] to hide internal
 * col() syntax from users
 *
 * @param {Token[]} tokens
 * @returns {string}
 */
function tokensToPreviewString(tokens) {
  const parts = []
  for (const t of tokens) {
    if (t.type === 'number') {
      parts.push(t.value)
    } else if (t.type === 'operator' || t.type === 'paren') {
      parts.push(t.value)
    } else if (t.type === 'col') {
      parts.push(`[${t.fileType || ''}.${t.columnName || ''}]`)
    } else if (t.type === 'string') {
      if (t.value === '') {
        parts.push('(空)')
      } else {
        parts.push(t.value)
      }
    }
  }
  return parts.join(' ')
}

/**
 * Check parenthesis balance in tokens (SR-135)
 *
 * @param {Token[]} tokens
 * @returns {{ valid: boolean; message?: string }}
 */
function checkParenthesisBalance(tokens) {
  let depth = 0
  for (const t of tokens) {
    if (t.type === 'paren') {
      if (t.value === '(') depth++
      else depth--
      if (depth < 0) return { valid: false, message: MSG_INVALID_EXPRESSION }
    }
  }
  if (depth !== 0) return { valid: false, message: MSG_INVALID_EXPRESSION }
  return { valid: true }
}

/**
 * Return true when a token can appear at the end of a value expression
 *
 * @param {Token} token
 * @returns {boolean}
 */
function isValueEndToken(token) {
  return (
    token.type === 'number' ||
    token.type === 'string' ||
    token.type === 'col' ||
    (token.type === 'paren' && token.value === ')')
  )
}

/**
 * Return true when a token can appear at the start of a value expression
 *
 * @param {Token} token
 * @returns {boolean}
 */
function isValueStartToken(token) {
  return (
    token.type === 'number' ||
    token.type === 'string' ||
    token.type === 'col' ||
    (token.type === 'paren' && token.value === '(')
  )
}

/**
 * Check token order to prevent implicit concatenation like "a a" or ") ("
 *
 * @param {Token[]} tokens
 * @returns {{ valid: boolean; message?: string }}
 */
function checkTokenOrder(tokens) {
  for (let i = 1; i < tokens.length; i++) {
    const prev = tokens[i - 1]
    const curr = tokens[i]
    if (isValueEndToken(prev) && isValueStartToken(curr)) {
      return { valid: false, message: MSG_INVALID_EXPRESSION }
    }
  }
  return { valid: true }
}

/**
 * Walk AST and check for division by zero (literal 0) (SR-136)
 *
 * @param {import('jsep').Expression} node
 * @returns {boolean} True if zero division found
 */
function hasZeroDivision(node) {
  if (!node) return false
  if (node.type === 'BinaryExpression') {
    if (node.operator === '/') {
      const right = node.right
      if (right?.type === 'Literal' && Number(right.value) === 0) return true
    }
    return hasZeroDivision(node.left) || hasZeroDivision(node.right)
  }
  if (node.type === 'CallExpression') return false
  if (node.type === 'UnaryExpression') return hasZeroDivision(node.argument)
  if (node.type === 'ConditionalExpression') {
    return hasZeroDivision(node.consequent) || hasZeroDivision(node.alternate)
  }
  return false
}

/**
 * Check whether a CallExpression is the supported col('fileType', 'columnName') form
 *
 * @param {import('jsep').Expression} node
 * @returns {boolean}
 */
function isSupportedColCall(node) {
  return (
    node?.type === 'CallExpression' &&
    node.callee?.type === 'Identifier' &&
    node.callee.name === 'col' &&
    Array.isArray(node.arguments) &&
    node.arguments.length === 2 &&
    node.arguments.every((arg) => arg?.type === 'Literal' && typeof arg.value === 'string')
  )
}

/**
 * Check whether the parsed AST contains only supported arithmetic expression nodes
 *
 * @param {import('jsep').Expression} node
 * @returns {boolean}
 */
function isSupportedExpressionAst(node) {
  if (!node) return false
  if (node.type === 'Literal') {
    return typeof node.value === 'number' && Number.isFinite(node.value)
  }
  if (node.type === 'BinaryExpression') {
    return (
      ['+', '-', '*', '/'].includes(node.operator) &&
      isSupportedExpressionAst(node.left) &&
      isSupportedExpressionAst(node.right)
    )
  }
  if (node.type === 'UnaryExpression') {
    return node.operator === '-' && isSupportedExpressionAst(node.argument)
  }
  if (node.type === 'CallExpression') {
    return isSupportedColCall(node)
  }
  if (node.type === 'Compound' || node.type === 'SequenceExpression') {
    return false
  }
  return false
}

/**
 * Convert JSEP AST to token array for UI (SR-113, DB→UI)
 *
 * @param {import('jsep').Expression} node
 * @returns {Token[]}
 */
function astToTokens(node) {
  if (!node) return []
  if (node.type === 'Literal') {
    const v = node.value
    return [{ type: 'number', value: typeof v === 'number' ? String(v) : String(v ?? '') }]
  }
  if (node.type === 'BinaryExpression') {
    const left = astToTokens(node.left)
    const right = astToTokens(node.right)
    const op = /** @type {TokenOperator['value']} */ (node.operator)
    return [...left, { type: 'operator', value: op }, ...right]
  }
  if (node.type === 'UnaryExpression') {
    const arg = astToTokens(node.argument)
    if (node.operator === '-') {
      return [{ type: 'operator', value: '-' }, ...arg]
    }
    return arg
  }
  if (node.type === 'CallExpression') {
    const callee = node.callee
    const name = callee?.type === 'Identifier' ? callee.name : ''
    if (name === 'col' && Array.isArray(node.arguments) && node.arguments.length >= 2) {
      const a0 = node.arguments[0]
      const a1 = node.arguments[1]
      const fileType = a0?.type === 'Literal' ? String(a0.value ?? '') : ''
      const columnName = a1?.type === 'Literal' ? String(a1.value ?? '') : ''
      return [{ type: 'col', fileType, columnName }]
    }
  }
  return []
}

/**
 * Parse initial expressionAst JSON and return token array
 *
 * @param {string} expressionAst
 * @returns {Token[]}
 */
function expressionAstToTokens(expressionAst) {
  if (!expressionAst || typeof expressionAst !== 'string') return []
  try {
    const ast = JSON.parse(expressionAst)
    return astToTokens(ast)
  } catch {
    return []
  }
}

/**
 * MappingInfo four_arithmetic → formData (DB→UI)
 *
 * @param {{
 *   mappingType?: string
 *   formState?: {
 *     tokens?: Token[]
 *     selectedFileType?: string
 *     selectedColumnName?: string
 *     numberInput?: string
 *   }
 *   FourArithmeticExpressions?: { expressionAst?: string; ExpressionAst?: string }
 *   fourArithmeticExpressions?: { expressionAst?: string; ExpressionAst?: string }
 * } | null} initial
 * @param {import('../extra/types').FileTypeInfo[]} fileTypes
 * @returns {{ tokens: Token[]; selectedFileType: string; selectedColumnName: string; numberInput: string }}
 */
export function mappingInfoToFormData(initial, fileTypes) {
  const formState = initial?.formState ?? initial?.form_state
  if (formState && typeof formState === 'object') {
    return {
      tokens: Array.isArray(formState.tokens) ? formState.tokens : [],
      selectedFileType: String(formState.selectedFileType ?? ''),
      selectedColumnName: String(formState.selectedColumnName ?? ''),
      numberInput: String(formState.numberInput ?? '')
    }
  }
  const expressionAst =
    initial?.fourArithmeticExpressions?.expressionAst ??
    initial?.fourArithmeticExpressions?.ExpressionAst ??
    initial?.FourArithmeticExpressions?.expressionAst ??
    initial?.FourArithmeticExpressions?.ExpressionAst ??
    ''
  const initialTokens = expressionAstToTokens(expressionAst)
  const firstFile = fileTypes?.[0]
  return {
    tokens: initialTokens,
    selectedFileType: firstFile?.mappingFileType ?? '',
    selectedColumnName: '',
    numberInput: ''
  }
}

/**
 * FormData → MappingInfo four_arithmetic (UI→DB)
 *
 * @param {{ tokens: Token[] }} formData
 * @returns {{
 *   mappingType: 'FourArithmeticExpressions'
 *   fourArithmeticExpressions: { expressionAst: string }
 *   formState: {
 *     tokens: Token[]
 *     selectedFileType?: string
 *     selectedColumnName?: string
 *     numberInput?: string
 *   }
 * } | null}
 */
export function formDataToMappingInfo(formData) {
  const validation = validateExpression(formData.tokens || [])
  if (!validation.valid || !validation.ast) return null
  try {
    const astJson = JSON.stringify(validation.ast)
    return {
      mappingType: 'FourArithmeticExpressions',
      fourArithmeticExpressions: { expressionAst: astJson },
      formState: { ...formData }
    }
  } catch {
    return null
  }
}

/**
 * Validate four_arithmetic form
 *
 * @param {{ tokens: Token[] }} formData
 * @returns {Record<string, string[]> | null}
 */
export function validateFourArithmeticForm(formData) {
  const validation = validateExpression(formData.tokens || [])
  if (!validation.valid) {
    return { expression: validation.errors }
  }
  return null
}

/**
 * Validate expression: required, syntax (JSEP), parenthesis, zero-division (SR-134, SR-135, SR-136, SR-137)
 *
 * @param {Token[]} tokens
 * @returns {{ valid: boolean; errors: string[]; ast?: import('jsep').Expression }}
 */
function validateExpression(tokens) {
  const errors = []
  if (!tokens || tokens.length === 0) {
    errors.push(MSG_EXPRESSION_REQUIRED)
    return { valid: false, errors }
  }
  const paren = checkParenthesisBalance(tokens)
  if (!paren.valid && paren.message) errors.push(paren.message)
  const tokenOrder = checkTokenOrder(tokens)
  if (!tokenOrder.valid && tokenOrder.message) errors.push(tokenOrder.message)
  if (errors.length > 0) return { valid: false, errors }
  const exprStr = tokensToExpressionString(tokens)
  let ast
  try {
    ast = jsep(exprStr)
  } catch (e) {
    errors.push(MSG_INVALID_EXPRESSION)
    return { valid: false, errors }
  }
  if (!isSupportedExpressionAst(ast)) {
    errors.push(MSG_INVALID_EXPRESSION)
    return { valid: false, errors }
  }
  if (hasZeroDivision(ast)) errors.push(MSG_INVALID_EXPRESSION)
  return {
    valid: errors.length === 0,
    errors,
    ast
  }
}

/**
 * FourArithmeticForm — 四則演算モーダル本体 (SR-122–SR-127)
 *
 * @param {{
 *   fileTypes: import('../extra/types').FileTypeInfo[]
 *   formData: { tokens: Token[]; selectedFileType: string; selectedColumnName: string; numberInput: string }
 *   errors: Record<string, string[]>
 *   onChange: (
 *     fd: { tokens: Token[]; selectedFileType: string; selectedColumnName: string; numberInput: string },
 *     setErrors: (e: Record<string, string[]>) => void
 *   ) => void
 *   setErrors?: (e: Record<string, string[]>) => void
 * }} props
 */
export const FourArithmeticForm = (props) => {
  const { fileTypes = [], formData, errors, onChange } = props

  const selectedFile = fileTypes.find((f) => f.mappingFileType === formData.selectedFileType)
  const fileTypeOptions = useMemo(
    () => fileTypes.map((f) => ({ label: f.mappingFileType, value: f.mappingFileType })),
    [fileTypes]
  )
  const columnOptions = useMemo(
    () => (selectedFile?.headers ?? []).map((h) => ({ label: h, value: h })),
    [selectedFile?.headers]
  )
  const isColumnDisabled = !formData.selectedFileType
  const isNumberAddDisabled = !String(formData.numberInput ?? '').trim()
  const previewString = tokensToPreviewString(formData.tokens || [])

  const appendToken = useCallback(
    (token) => {
      const next = [...(formData.tokens || []), token]
      onChange({ ...formData, tokens: next }, () => {})
    },
    [formData, onChange]
  )

  const appendOperator = useCallback((op) => appendToken({ type: 'operator', value: op }), [appendToken])
  const appendParen = useCallback((p) => appendToken({ type: 'paren', value: p }), [appendToken])
  const appendCol = useCallback(() => {
    if (!formData.selectedFileType || !formData.selectedColumnName) return
    appendToken({
      type: 'col',
      fileType: formData.selectedFileType,
      columnName: formData.selectedColumnName
    })
  }, [formData.selectedFileType, formData.selectedColumnName, appendToken])

  const appendNumber = useCallback(() => {
    const v = String(formData.numberInput ?? '').trim()
    if (v === '') return
    const next = [...(formData.tokens || []), { type: 'number', value: v }]
    onChange({ ...formData, tokens: next, numberInput: '' }, () => {})
  }, [formData, onChange])

  const clearTokens = useCallback(() => {
    onChange({ ...formData, tokens: [], numberInput: '' }, () => {})
  }, [formData, onChange])

  const backspaceTokens = useCallback(() => {
    const tokens = formData.tokens || []
    if (tokens.length === 0) return
    onChange({ ...formData, tokens: tokens.slice(0, -1) }, () => {})
  }, [formData, onChange])

  // モックアップ準拠: 演算子は内部は */ で、表示は ×÷
  const operatorSpecs = [
    { op: '+', label: '+' },
    { op: '-', label: '-' },
    { op: '*', label: '×' },
    { op: '/', label: '÷' }
  ]

  return (
    <div className="text-h7">
      {/* 計算式の作成 */}
      <section className="mb-4">
        <label className="block mb-1.5 font-medium text-[13px] text-[#555]">計算式の作成</label>
        <div
          className="bg-white border border-[#ddd] rounded p-2.5 min-h-[40px] mb-3 font-mono text-[14px] break-all"
          data-testid="four-arithmetic-preview"
          style={{ color: previewString ? undefined : '#999' }}
        >
          {previewString || 'ここに計算式が表示されます'}
        </div>
        <div className="flex flex-wrap gap-2.5 mb-2.5">
          <button
            type="button"
            className="px-4 py-2 border border-[#ddd] bg-[#fff3cd] rounded cursor-pointer text-[13px] hover:bg-[#e8f4f8] hover:border-[#3150A5]"
            onClick={() => appendParen('(')}
            data-testid="four-arithmetic-paren-open"
          >
            (
          </button>
          <button
            type="button"
            className="px-4 py-2 border border-[#ddd] bg-[#fff3cd] rounded cursor-pointer text-[13px] hover:bg-[#e8f4f8] hover:border-[#3150A5]"
            onClick={() => appendParen(')')}
            data-testid="four-arithmetic-paren-close"
          >
            )
          </button>
          {operatorSpecs.map(({ op, label }) => (
            <button
              key={op}
              type="button"
              className="px-4 py-2 border border-[#ddd] bg-[#E0E5F2] rounded cursor-pointer text-[13px] font-bold hover:bg-[#e8f4f8] hover:border-[#3150A5]"
              onClick={() => appendOperator(op)}
              data-testid={`four-arithmetic-op-${op}`}
            >
              {label}
            </button>
          ))}
          <button
            type="button"
            className="px-4 py-2 border border-[#ddd] bg-[#ffebee] rounded cursor-pointer text-[13px] hover:bg-[#e8f4f8] hover:border-[#3150A5]"
            onClick={clearTokens}
            data-testid="four-arithmetic-clear"
          >
            クリア
          </button>
          <button
            type="button"
            className="px-4 py-2 border border-[#ddd] bg-[#fff3cd] rounded cursor-pointer text-[13px] hover:bg-[#e8f4f8] hover:border-[#3150A5]"
            onClick={backspaceTokens}
            data-testid="four-arithmetic-backspace"
          >
            ← 削除
          </button>
        </div>
        <ChrofyShowErrors field="expression" errors={errors} />
      </section>

      {/* 項目を追加 */}
      <section className="mb-4">
        <label className="block mb-1.5 font-medium text-[13px] text-[#555]">項目を追加</label>
        <div className="flex items-center gap-2">
          <ChrofyCustomSelect
            id="four-arithmetic-file-type"
            name="four-arithmetic-file-type"
            value={formData.selectedFileType}
            onChange={(e) =>
              onChange({ ...formData, selectedFileType: e.target.value, selectedColumnName: '' }, () => {})
            }
            className="flex-1 min-w-0"
            options={fileTypeOptions}
            disabled={false}
            hasNoSelect={true}
            searchable
            placeholder="ファイル種別を選択"
            data-testid="four-arithmetic-file-type"
          />
          <ChrofyCustomSelect
            id="four-arithmetic-column-name"
            name="four-arithmetic-column-name"
            value={formData.selectedColumnName}
            onChange={(e) => onChange({ ...formData, selectedColumnName: e.target.value }, () => {})}
            className={`flex-1 min-w-0${isColumnDisabled ? ' opacity-60' : ''}`}
            options={columnOptions}
            disabled={isColumnDisabled}
            hasNoSelect={true}
            searchable
            placeholder="項目を選択"
            data-testid="four-arithmetic-column-name"
          />
          <button
            type="button"
            className="px-2.5 py-1 text-[12px] text-white rounded cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
            style={{ background: 'linear-gradient(135deg, #3150A5 0%, #4a6bb5 100%)' }}
            onClick={appendCol}
            disabled={isColumnDisabled || !formData.selectedColumnName}
            data-testid="four-arithmetic-add-col"
          >
            項目追加
          </button>
        </div>
      </section>

      {/* 固定値を追加 */}
      <section className="mb-4">
        <label className="block mb-1.5 font-medium text-[13px] text-[#555]">固定値を追加</label>
        <div className="flex items-center gap-2">
          <input
            type="text"
            className="text-input text-left"
            style={{ flex: 1, maxWidth: '200px', height: '34px', boxSizing: 'border-box' }}
            value={formData.numberInput ?? ''}
            onChange={(e) => onChange({ ...formData, numberInput: e.target.value }, () => {})}
            placeholder="例: 100 または 0.5"
            data-testid="four-arithmetic-number-input"
          />
          <button
            type="button"
            className="px-2.5 py-1 text-[12px] text-[#3150A5] bg-[#E0E5F2] rounded cursor-pointer hover:bg-[#d0d5e2] disabled:opacity-60 disabled:cursor-not-allowed"
            onClick={appendNumber}
            disabled={isNumberAddDisabled}
            data-testid="four-arithmetic-add-number"
          >
            固定値追加
          </button>
        </div>
      </section>
    </div>
  )
}

export default FourArithmeticForm
